/**
 * VisionWSClient — WebSocket client that streams rrweb events to the
 * vision loop backend and receives intervention commands.
 *
 * Upstream messages:  init, rrweb, action_complete, conversation_started, conversation_ended
 * Downstream messages: suggest_intervention, status
 */

import type { eventWithTime } from '@rrweb/types'

// ---------------------------------------------------------------------------
// Types — match the WebSocket protocol defined in the v3 PRD §4.3
// ---------------------------------------------------------------------------

export interface InitMessage {
  type: 'init'
  staffId: string
  visitorId: string
  pageUrl: string
  pageTitle: string
}

export interface InterventionMessage {
  type: 'suggest_intervention'
  actionId: string
  message: string
  goalId: string
  context: string // rolling summary for conversation injection
}

export interface StatusMessage {
  type: 'status'
  state: 'observing' | 'paused' | 'acting'
}

export type ActionResult = 'accepted' | 'declined' | 'ignored'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Resolve a potentially relative WebSocket URL against the page location. */
function resolveWsUrl(url: string): string {
  if (url.startsWith('ws://') || url.startsWith('wss://')) return url
  // Relative path — derive from page host
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${location.host}${url.startsWith('/') ? '' : '/'}${url}`
}

const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30_000
const EVENT_BUFFER_MAX = 500

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export class VisionWSClient {
  // --- Callbacks ---
  onIntervention: ((msg: InterventionMessage) => void) | null = null
  onStatus: ((msg: StatusMessage) => void) | null = null
  onConnected: (() => void) | null = null
  onDisconnected: (() => void) | null = null

  // --- Internal ---
  private ws: WebSocket | null = null
  private url: string
  private initPayload: InitMessage
  private eventBuffer: object[] = []
  private reconnectDelay = RECONNECT_BASE_MS
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private _connected = false
  private _closed = false

  constructor(url: string, init: Omit<InitMessage, 'type'>) {
    this.url = resolveWsUrl(url)
    this.initPayload = { type: 'init', ...init }
  }

  get isConnected(): boolean {
    return this._connected
  }

  // -----------------------------------------------------------------------
  // Public — lifecycle
  // -----------------------------------------------------------------------

  /** Open the WebSocket connection. Automatically reconnects on failure. */
  connect(): void {
    if (this._closed) return
    this.doConnect()
  }

  /** Close permanently (no reconnect). */
  close(): void {
    this._closed = true
    this.clearReconnectTimer()
    if (this.ws) {
      try { this.ws.close() } catch {}
      this.ws = null
    }
    this._connected = false
  }

  // -----------------------------------------------------------------------
  // Public — upstream messages
  // -----------------------------------------------------------------------

  /** Stream an rrweb event to the backend. */
  sendRRWebEvent(event: eventWithTime): void {
    this.send({ type: 'rrweb', event })
  }

  /** Signal that the user responded to an intervention suggestion. */
  sendActionComplete(actionId: string, result: ActionResult): void {
    this.send({ type: 'action_complete', actionId, result })
  }

  /** Signal that a voice conversation has started. */
  sendConversationStarted(): void {
    this.send({ type: 'conversation_started' })
  }

  /** Signal that a voice conversation has ended. */
  sendConversationEnded(summary: string): void {
    this.send({ type: 'conversation_ended', summary })
  }

  // -----------------------------------------------------------------------
  // Private — connection
  // -----------------------------------------------------------------------

  private doConnect(): void {
    if (this._closed) return

    try {
      this.ws = new WebSocket(this.url)
    } catch (err) {
      console.warn('[VisionWS] Failed to create WebSocket:', err)
      this.scheduleReconnect()
      return
    }

    this.ws.onopen = () => {
      console.log('[VisionWS] Connected')
      this._connected = true
      this.reconnectDelay = RECONNECT_BASE_MS

      // Send init message first
      this.ws!.send(JSON.stringify(this.initPayload))

      // Flush buffered events
      this.flushBuffer()

      this.onConnected?.()
    }

    this.ws.onmessage = (e) => {
      const data = e.data
      if (typeof data === 'string') {
        this.parseAndHandle(data)
      } else if (data instanceof Blob) {
        data.text().then(text => this.parseAndHandle(text))
      } else if (data instanceof ArrayBuffer) {
        this.parseAndHandle(new TextDecoder().decode(data))
      } else {
        console.warn('[VisionWS] Unknown message data type:', typeof data)
      }
    }

    this.ws.onclose = () => {
      const wasConnected = this._connected
      this._connected = false
      this.ws = null

      if (wasConnected) {
        console.log('[VisionWS] Disconnected')
        this.onDisconnected?.()
      }

      if (!this._closed) {
        this.scheduleReconnect()
      }
    }

    this.ws.onerror = (err) => {
      console.warn('[VisionWS] Error:', err)
      // onclose will fire after onerror, triggering reconnect
    }
  }

  private parseAndHandle(text: string): void {
    try {
      const msg = JSON.parse(text)
      switch (msg.type) {
        case 'suggest_intervention':
          this.onIntervention?.(msg as InterventionMessage)
          break
        case 'status':
          this.onStatus?.(msg as StatusMessage)
          break
        default:
          console.log('[VisionWS] Unknown message type:', msg.type)
      }
    } catch (err) {
      console.warn('[VisionWS] Failed to parse message:', err)
    }
  }

  // -----------------------------------------------------------------------
  // Private — send + buffer
  // -----------------------------------------------------------------------

  private send(msg: object): void {
    if (this._connected && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg))
    } else {
      // Buffer events while disconnected (drop oldest if full)
      if (this.eventBuffer.length >= EVENT_BUFFER_MAX) {
        this.eventBuffer.shift()
      }
      this.eventBuffer.push(msg)
    }
  }

  private flushBuffer(): void {
    if (!this._connected || !this.ws) return
    for (const msg of this.eventBuffer) {
      this.ws.send(JSON.stringify(msg))
    }
    if (this.eventBuffer.length > 0) {
      console.log(`[VisionWS] Flushed ${this.eventBuffer.length} buffered messages`)
    }
    this.eventBuffer = []
  }

  // -----------------------------------------------------------------------
  // Private — reconnect
  // -----------------------------------------------------------------------

  private scheduleReconnect(): void {
    if (this._closed || this.reconnectTimer) return

    const delay = this.reconnectDelay
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_MS)

    console.log(`[VisionWS] Reconnecting in ${delay}ms...`)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.doConnect()
    }, delay)
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }
}
